import React, { useEffect } from 'react';
import { Menu, MenuProps, Spin, Tooltip } from 'antd';
import Sider from 'antd/es/layout/Sider';
import './Sider.scss';
import { ResellerSiderItem } from 'src/enums/IResellerSiderItems';
import { useMutation } from '@tanstack/react-query';
import { useAppSelector } from '@/store/hooks/useAppSelector';
import { getLoggedInUser, getSelectedAgentId } from '@/store/account/selector';
import { getProvisionedNumber } from '@/api/reservedNumbers';
import { useDispatch } from 'react-redux';
import { setCurrentPage } from '@/store/account/slice';
import { ReceptionistDashboardSiderItem } from 'src/enums/EReceptionistDashboardSiderItem';
import { CallOutlined } from '@mui/icons-material';
import LowCreditsWarning from './LowCreditsWarning/LowCreditsWarning';
import { DESKTOP_LOGO_S3_URL } from 'src/constants';
import SiderCreditsStatus from './SiderCreditsStatus';
import { setPhoneNumber } from '@/store/phoneNumber/slice';
import { getPhoneNumber } from '@/store/phoneNumber/selector';

interface CommonSiderProps {
	items: MenuProps['items'];
	logo: React.ReactNode;
	activeMenuItem: ResellerSiderItem | string;
	className?: string;
	menuRef?: React.RefObject<HTMLDivElement>;
	handleClick: MenuProps['onClick'];
	setOpenedOnboarding?: any;
	openedOnboarding?: boolean;
	tourOpen?: boolean;
	debrand?: boolean;
	openKeys?: string[];
	onOpenChange?: (openKeys: string[]) => void;
	resellerSider?: boolean;
	width?: number;
	hideLogo?: boolean;
}

const CommonSider: React.FC<CommonSiderProps> = ({
	items,
	logo,
	activeMenuItem,
	className = '',
	menuRef,
	handleClick,
	openKeys,
	onOpenChange,
	setOpenedOnboarding,
	resellerSider,
	width,
	hideLogo = false,
}) => {
	const finalizedMenuItems = items ? [...items] : [];

	const baseClass = 'common-sider';
	const loggedInUser = useAppSelector(getLoggedInUser);
	const dispatch = useDispatch();
	const selectedAgentId = useAppSelector(getSelectedAgentId);
	const phoneNumber = useAppSelector(getPhoneNumber);

	// const featureFlags = useAppSelector(getFeatureFlags);
	// const registrationFeatureFlagKey = featureFlags.ONBOARDING_REGISTRATION_REVAMP;
	// const newRegistrationFlow =
	//   registrationFeatureFlagKey === POSTHOG_FEATURE_FLAGS.ONBOARDING_REGISTRATION_REVAMP.variants.NEW;
	// const [isModalOpen, setIsModalOpen] = useState(false);
	// const currentReseller = useAppSelector(getResellerData);

	// const showModal = () => {
	//   setIsModalOpen(true);
	// };

	// const handleOk = () => {
	//   setIsModalOpen(false);
	// };

	const getProvisionNumberMutation = useMutation({
		mutationKey: ['provisionedNumberApi'],
		mutationFn: getProvisionedNumber,
		onSuccess(data) {
			dispatch(setPhoneNumber({ phoneNumber: data.provisioned_number || '' }));
		},
	});

	useEffect(() => {
		if (selectedAgentId) {
			getProvisionNumberMutation.mutate(selectedAgentId as string);
		}
	}, [selectedAgentId]);

	const handleReservedNumberClick = () => {
		if (phoneNumber || (!phoneNumber && !loggedInUser?.inboundDemo)) {
			dispatch(setCurrentPage(ReceptionistDashboardSiderItem.GET_STARTED));
			return;
		}
		if (!phoneNumber && loggedInUser?.inboundDemo) {
			setOpenedOnboarding(true);
			dispatch(setCurrentPage(ReceptionistDashboardSiderItem.HIRE_AI_RECEPTIONIST));
		}
	};

	return (
		<Sider
			collapsed={false}
			className={`${baseClass} ${className}`}
			collapsible={false}
			theme="light"
			breakpoint="md"
			width={width}
		>
			{resellerSider ? (
				hideLogo ? null : (
					logo
				)
			) : loggedInUser.resellerClient ? (
				logo
			) : (
				<img src={DESKTOP_LOGO_S3_URL} alt="My Logo Sider" />
			)}
			{!resellerSider && selectedAgentId && (
				<Spin spinning={getProvisionNumberMutation.isPending}>
					<div className="flex items-center justify-center reserved-number px-3 py-2">
						<div
							onClick={handleReservedNumberClick}
							className={`inline-flex mx-auto cursor-pointer rounded-lg bg-blue-50 w-full items-center p-2  px-4 text-button-success ${loggedInUser?.inboundDemo ? 'text-red-600' : ''}`}
						>
							<Tooltip
								title={
									phoneNumber
										? "Your AI's reserved phone number. Click for more info."
										: 'Click here to reserve a phone number for your AI.'
								}
								className={`sm:${phoneNumber ? 'text-sm' : 'text-sm'}  ${loggedInUser?.inboundDemo ? 'text-red-700' : ''}`}
							>
								<CallOutlined fontSize="medium" />{' '}
								{phoneNumber
									? phoneNumber
									: loggedInUser?.inboundDemo
										? 'Start free trial'
										: 'No reserved number'}
							</Tooltip>
						</div>
					</div>
				</Spin>
			)}

			<div className="my-3 px-3">
				<LowCreditsWarning forReseller={resellerSider} />
			</div>

			<div ref={menuRef} className="mt-3">
				<Menu
					items={finalizedMenuItems}
					selectedKeys={[activeMenuItem]}
					onClick={handleClick}
					mode="inline"
					onOpenChange={onOpenChange}
					defaultOpenKeys={openKeys}
				/>
			</div>
			{!resellerSider && (
				<div className="my-3 px-3">
					<SiderCreditsStatus forReseller={resellerSider} />
				</div>
			)}
		</Sider>
	);
};

export default CommonSider;
